import {makeAutoObservable, observable} from "mobx";
import React, {useEffect} from "react";
import type {RootStore} from "stores/rootStore";
import {rootStore} from "stores/rootStore";

export interface ModalProps {
    title?: string
    onOk?: () => void
    onCancel?: (e?: MouseEvent) => void
    width?: number
    open?: boolean
    icon?: React.ReactNode
}

const MODALS: {
    [key: string]: React.ReactElement<ModalProps>
} = {}

export const __MODALS = MODALS;

interface PendingModalRequest {
    id: string
    props?: object
}

/**
 * The subset of `props` that survives postMessage's structured clone — in practice, everything
 * that isn't (or doesn't contain) a callback. A delegated modal lives in the host page, so its
 * buttons can't call back into this frame anyway; passing a function throws DataCloneError and
 * loses the WHOLE modal.
 *
 * A prop is dropped whole rather than stripped in place: half a step list (its `onApply` gone)
 * would render CTAs that do nothing. Filtered here rather than at each call site, because
 * showModal is the code that decides to delegate — every callback prop added from now on is
 * safe by construction instead of by the next author remembering.
 */
const cloneable = (props?: object): object | undefined => {
    if (!props) return props
    return Object.fromEntries(Object.entries(props).filter(([, value]) => isCloneable(value)))
}

/** Whether one prop value can cross postMessage. `structuredClone` is the exact same algorithm
 *  postMessage uses, so trying it is the only reliable test — but it only landed in Chrome 98 /
 *  Safari 15.4, and this must not degrade into "send it anyway" on older engines: the throw would
 *  come out of showModal and the delegated modal would never appear at all. Without it, fall back
 *  to rejecting anything that holds a function, which is the only thing we actually pass. */
const isCloneable = (value: unknown): boolean => {
    if (typeof structuredClone === 'function') {
        try {
            structuredClone(value)
            return true
        } catch {
            return false
        }
    }
    return !holdsFunction(value)
}

const holdsFunction = (value: unknown, depth = 0): boolean => {
    if (typeof value === 'function') return true
    if (value === null || typeof value !== 'object' || depth >= 6) return false
    return Object.values(value as Record<string, unknown>).some(v => holdsFunction(v, depth + 1))
}

export class ModalStore {
    modalComponent: React.ReactNode = null
    badgeComponent: React.ReactNode = null
    delegatedModals: Set<string> = new Set()
    pendingModal: PendingModalRequest | null = null
    root: RootStore

    constructor(root: RootStore) {
        this.root = root
        makeAutoObservable(this, {
            modalComponent: observable.ref,
            badgeComponent: observable.ref,
        });
    }

    getModalById(id: string) {
        return MODALS[id]
    }

    registerModal(id: string, modal: React.ReactElement<ModalProps>) {
        MODALS[id] = modal

        if (this.pendingModal && this.pendingModal.id === id) {
            const {props} = this.pendingModal
            this.pendingModal = null
            this.setModal(modal, props)
        }
    }

    isModalOpen() {
        return !!this.modalComponent
    }

    showModal(id: string, props?: object) {
        console.log("Show modal", id)
        if (this.root.widgetMode && this.delegatedModals.has(id)) {
            window.parent.postMessage({type: "show-modal", modalId: id, props: cloneable(props)}, "*");
            return;
        }

        const modal = this.getModalById(id)
        if (!modal) {
            this.pendingModal = {id, props}
            return
        }
        this.pendingModal = null
        this.setModal(modal, props);
    }

    setBadge() {

    }

    setModal(component: React.ReactElement<ModalProps>, extraProps?: object) {
        console.log("Set modal", component)
        const originalOnCancel = () => component.props.onCancel?.();
        const originalOnOk = () => component.props.onOk?.();

        this.modalComponent = React.cloneElement(component, {
            ...component.props,
            open: true,
            onCancel: (e?: MouseEvent) => {
                e?.stopPropagation?.();
                originalOnCancel();
                this.closeModal();
            },
            onOk: () => {
                this.closeModal();
                originalOnOk();
            },
            ...extraProps
        });
    }

    closeModal() {
        console.log("Close modal")
        this.modalComponent = null
    }
}
